import { useLazyQuery } from '@apollo/client';
import {
  ChannelStatistics,
  GET_CHANNEL_STATISTICS,
} from '@frontend/data-access';
import { Segmented } from 'antd';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import DeliveryDuration from '../delivery-duration/delivery-duration';
import IdeaCountByStatus from '../idea-count-by-status/idea-count-by-status';
import IdeaCountsByChannel from '../idea-counts-by-channel/idea-counts-by-channel';
import IdeaCountsByDays from '../idea-counts-by-days/idea-counts-by-days';
import IdeaCountsByStages from '../idea-counts-by-stages/idea-counts-by-stages';
import IdeaCountsByTags from '../idea-counts-by-tags/idea-counts-by-tags';
import IdeasAssignedToReviewers from '../ideas-assigned-to-reviewers/ideas-assigned-to-reviewers';
import StrategicDrivers from '../strategic-drivers/strategic-drivers';
import { ChannelSelector } from './components/channel-selector';
import { MetricsDashboard } from './metrics-dashboard/metrics-dashboard';

export function DashboardView() {
  const router = useRouter();
  const { tab } = router.query;
  const [selectedChannel, setSelectedChannel] = useState<string>();
  const [selectedSegment, setSelectedSegment] = useState<string>('channels');
  const [selectedChallenge, setSelectedChallenge] = useState<string>();
  const [selectedInnovationSpace, setSelectedInnovationSpace] =
    useState<string>();

  const [getStatistics] = useLazyQuery<{
    getChannelStatistics: ChannelStatistics;
  }>(GET_CHANNEL_STATISTICS);

  const handleSegmentChange = (value: string | number) => {
    router.push({
      query: { ...router.query, tab: value.toString() },
    });
  };

  const renderTab = () => {
    switch (selectedSegment) {
      case 'channels':
        return (
          <>
            <ChannelSelector
              selectedChannel={selectedChannel}
              selectedChallenge={selectedChallenge}
              selectedInnovationSpace={selectedInnovationSpace}
              onChannelChange={setSelectedChannel}
              onChallengeChange={setSelectedChallenge}
              onInnovationSpaceChange={setSelectedInnovationSpace}
            />
            <div className="flex flex-col gap-4">
              <div className="grid md:grid-cols-2 grid-cols-1 flex-wrap gap-4">
                <IdeaCountsByStages
                  channelId={selectedChannel}
                  challengeId={selectedChallenge}
                  innovationSpaceId={selectedInnovationSpace}
                />
                <IdeaCountByStatus
                  channelId={selectedChannel}
                  challengeId={selectedChallenge}
                  innovationSpaceId={selectedInnovationSpace}
                />
              </div>
              <IdeaCountsByDays
                channelId={selectedChannel}
                challengeId={selectedChallenge}
                innovationSpaceId={selectedInnovationSpace}
              />
              <div className="grid md:grid-cols-2 grid-cols-1 flex-wrap gap-4">
                <IdeaCountsByTags
                  channelId={selectedChannel}
                  challengeId={selectedChallenge}
                  innovationSpaceId={selectedInnovationSpace}
                />
                <IdeaCountsByChannel
                  channelId={selectedChannel}
                  challengeId={selectedChallenge}
                  innovationSpaceId={selectedInnovationSpace}
                />
              </div>
              <div className="grid md:grid-cols-2 grid-cols-1 flex-wrap gap-4">
                <StrategicDrivers
                  channelId={selectedChannel}
                  challengeId={selectedChallenge}
                  innovationSpaceId={selectedInnovationSpace}
                />
                <DeliveryDuration
                  channelId={selectedChannel}
                  challengeId={selectedChallenge}
                  innovationSpaceId={selectedInnovationSpace}
                />
              </div>
              <IdeasAssignedToReviewers
                channelId={selectedChannel}
                challengeId={selectedChallenge}
                innovationSpaceId={selectedInnovationSpace}
              />
            </div>
          </>
        );
      case 'metrics':
        return <MetricsDashboard />;

      default:
        return;
    }
  };

  useEffect(() => {
    if (tab === 'metrics' || tab === 'channels') {
      setSelectedSegment(tab);
    } else if (!tab) {
      router.replace({
        query: { ...router.query, tab: 'channels' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (selectedChannel) {
      getStatistics({ variables: { channelId: selectedChannel } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannel]);

  return (
    <div className="dashboard-container space-y-4">
      <div className="bg-gray-100 p-4 rounded-2xl flex items-center">
        <Segmented
          rootClassName="overflow-x-auto"
          value={selectedSegment}
          onChange={handleSegmentChange}
          options={[
            { value: 'channels', label: 'Idea channels' },
            { value: 'metrics', label: 'Idea metrics' },
          ]}
        />
      </div>
      {renderTab()}
    </div>
  );
}

export default DashboardView;
